import { Code, ConnectError } from '@connectrpc/connect';
import { isAxiosError } from 'axios';

function retrySeconds(header: unknown): number | undefined {
  if (typeof header !== 'string' || !/^[1-9]\d{0,3}$/.test(header)) {
    return undefined;
  }
  const seconds = Number(header);
  return seconds <= 3600 ? seconds : undefined;
}

/** Only the verified refusal shape reaches the operator, never backend error text. */
export function fdbReadLimit(error: unknown): { retryAfterSeconds: number | undefined } | null {
  if (error instanceof ConnectError && error.code === Code.ResourceExhausted) {
    return { retryAfterSeconds: retrySeconds(error.metadata.get('retry-after')) };
  }
  if (isAxiosError(error) && error.response?.status === 429) {
    const headers = error.response.headers;
    return { retryAfterSeconds: retrySeconds(headers?.['retry-after'] ?? headers?.['Retry-After']) };
  }
  return null;
}

function fdbServiceUnavailable(error: unknown): boolean {
  return (error instanceof ConnectError && error.code === Code.Unavailable)
    || (isAxiosError(error) && error.response?.status === 503);
}

/** Distinguish installation policy from unrelated device preconditions by code, not text. */
export function fdbTargetDenied(error: unknown): boolean {
  if (error instanceof ConnectError) {
    return error.code === Code.FailedPrecondition
      && error.metadata.get('X-WorkerNet-Reason') === 'direct_snmp_target_denied';
  }
  if (isAxiosError(error) && error.response?.status === 422) {
    const data: unknown = error.response.data;
    return data !== null && typeof data === 'object'
      && 'error' in data && data.error === 'direct_snmp_target_denied';
  }
  return false;
}

export function fdbReadRefused(error: unknown): boolean {
  return fdbReadLimit(error) !== null || fdbServiceUnavailable(error) || fdbTargetDenied(error);
}

export function retryFDBQuery(failureCount: number, error: unknown): boolean {
  if (fdbReadRefused(error)) {
    return false;
  }
  return failureCount < 3;
}

/** Block automatic refetch of the same refused query, not manual refresh or a new key. */
export function fdbQueryEnabled(
  requested: boolean,
  error: unknown,
  errorUpdatedAt: number,
  now = Date.now(),
): boolean {
  if (!requested) {
    return false;
  }
  // No retry deadline on a 503 or policy denial: explicit refresh/new key only.
  if (fdbServiceUnavailable(error) || fdbTargetDenied(error)) {
    return false;
  }
  const refusal = fdbReadLimit(error);
  if (refusal === null) {
    return true;
  }
  const seconds = refusal.retryAfterSeconds;
  return seconds !== undefined && Number.isFinite(errorUpdatedAt) && errorUpdatedAt > 0
    && now >= errorUpdatedAt + seconds * 1000;
}
